/**
 * @fileoverview Saved report library (MOR-906 §7.8)
 *
 * The whole set of saved tables, on a tab of its own. Opening one — or
 * starting a new one — raises the same builder modal the Dashboard uses, so
 * a report is composed the same way wherever you begin. Saving here leaves it
 * in the library; starring is what pins it to the board.
 *
 * The starter set includes a REM pack table to build on, so the remuneration
 * matrix is a report a firm can reshape rather than a fixed screen.
 *
 * Starring a table pins it to the Dashboard board instead, which is where the
 * smaller ones belong.
 *
 * @component
 * @example
 * <ReportsLibrary orgId={orgId} />
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { Star, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TimePivotSpec } from "@cvx/timesheets/savedPivotReports/schema";

import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives";
import { Spinner } from "@/ui/primitives/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import type { AnalyticsAnswer } from "./AskAnalyticsBox";
import { formatDateRange } from "./format";
import { DIMENSION_LABELS, PERIOD_LABELS } from "./pivotExport";

const CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] overflow-hidden";

/** "Person by Category", or just the rows when there is no column axis. */
function shapeOf(spec: TimePivotSpec): string {
  const rows = spec.rows.map((r) => DIMENSION_LABELS[r]).join(" then ");
  const columns = spec.columns.map((c) => DIMENSION_LABELS[c]).join(", ");
  return columns ? `${rows} by ${columns}` : rows;
}

function periodOf(spec: TimePivotSpec): string {
  if (spec.preset === "custom" && spec.startDate && spec.endDate) {
    return formatDateRange(spec.startDate, spec.endDate);
  }
  return PERIOD_LABELS[spec.preset] ?? spec.preset;
}

export function ReportsLibrary({
  orgId,
  onOpenReport,
}: {
  orgId: Id<"organizations">;
  /** Raise the builder modal, which the page owns, on this report. */
  onOpenReport: (report: AnalyticsAnswer) => void;
}) {
  const { data: saved } = useQuery(
    convexQuery(api.timesheets.pivotReports.listSaved, { orgId }),
  );
  const setFavourite = useMutation(api.timesheets.pivotReports.setFavourite);
  const deleteReport = useMutation(api.timesheets.pivotReports.deleteReport);

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {saved === undefined ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner className="text-muted-foreground" />
        </div>
      ) : saved.length === 0 ? (
        <div className="flex h-40 flex-col items-center justify-center gap-1 rounded-xl bg-background text-sm text-muted-foreground shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
          <p>No saved reports yet.</p>
          <p className="text-xs">
            Build one to cross people, matters and task areas the way a
            remuneration appendix does, then export it to Excel.
          </p>
        </div>
      ) : (
        <div className={CARD_CLASS}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Report</TableHead>
                  <TableHead>Shape</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {saved.map((report) => (
                  <TableRow key={report._id}>
                    <TableCell>
                      <button
                        type="button"
                        className="text-left text-sm font-medium hover:text-primary"
                        onClick={() =>
                          onOpenReport({
                            shape: "pivot",
                            spec: report.spec,
                            title: report.name,
                          })
                        }
                        title="Open this report in the builder"
                      >
                        {report.name}
                      </button>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {shapeOf(report.spec)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {periodOf(report.spec)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {report.spec.scope === "org" ? "Firm" : "My time"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(
                            "size-7",
                            report.isFavourite
                              ? "text-amber-500"
                              : "text-muted-foreground",
                          )}
                          onClick={() =>
                            setFavourite({
                              reportId: report._id,
                              isFavourite: !report.isFavourite,
                            }).catch((err: unknown) =>
                              toast.error(
                                extractErrorMessage(
                                  err,
                                  "Could not star the report.",
                                ),
                              ),
                            )
                          }
                          aria-label={
                            report.isFavourite
                              ? `Remove ${report.name} from the Dashboard`
                              : `Pin ${report.name} to the Dashboard`
                          }
                          title={
                            report.isFavourite
                              ? "Remove from the Dashboard board"
                              : "Pin to the Dashboard board"
                          }
                        >
                          <Star
                            className={cn(
                              "size-3.5",
                              report.isFavourite && "fill-current",
                            )}
                          />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            deleteReport({ reportId: report._id }).catch(
                              (err: unknown) =>
                                toast.error(
                                  extractErrorMessage(
                                    err,
                                    "Could not delete the report.",
                                  ),
                                ),
                            )
                          }
                          aria-label={`Delete ${report.name}`}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
